import 'animate.css'
import '@afgri/tokens/tokens.css'

import { GoogleTagManager } from '@next/third-parties/google'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import minMax from 'dayjs/plugin/minMax'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import omit from 'lodash-es/omit'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { useReportWebVitals } from 'next/web-vitals'

import { ThemeProvider } from '@/components/ThemeProvider'
import { ToastContainer } from '@/components/Toaster'
import { AnalyticsDataProvider } from '@/feature/analytics/components/AnalyticsDataProvider'
import { FeatureFlagProvider } from '@/feature/feature-flags'
import { AssetTelemetryConnection } from '@/feature/telemetry'
import { VersionChecker } from '@/feature/version'
import { GlobalModals } from '@/lib/components/GlobalModals'
import isServer from '@/utils/isServer'

import { Layout } from '@/lib/core/components/Layout'
import { Session } from '@/lib/core/components/Session'
import { useTheme } from '@/lib/core/hooks/useTheme'

import { GlobalStyle } from '@/lib/themes/globalStyles'

dayjs.extend(isBetween)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(minMax)
dayjs.extend(weekOfYear)

if (!isServer) {
  /**
   * Google maps Advanced Map Marker uses shadowRoot to render
   * and will add additional unconfigurable elements to the shadowRoot if it is closed
   */
  const originalShadow = Element.prototype.attachShadow
  Element.prototype.attachShadow = function (options: ShadowRootInit) {
    const shadowRoot = originalShadow.call(this, { ...options, mode: 'open' })

    this.dispatchEvent(new CustomEvent('shadowRootOpened', { detail: { shadowRoot, options } }))

    return shadowRoot
  }
}

const MyApp = ({ Component, pageProps }: AppProps) => {
  const theme = useTheme()

  const cleanedPageProps = omit(pageProps, ['messages'])

  return (
    <ThemeProvider theme={{ ...theme }}>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1"
        />
        <title>Bridge - GoMetro</title>
      </Head>
      <GlobalStyle />
      <Session>
        <FeatureFlagProvider>
          <AnalyticsDataProvider>
            <VersionChecker>
              <AssetTelemetryConnection />
              <Layout>
                <Component {...cleanedPageProps} />
              </Layout>
              <GlobalModals />
            </VersionChecker>
            <ToastContainer />
          </AnalyticsDataProvider>
        </FeatureFlagProvider>
      </Session>
    </ThemeProvider>
  )
}

const WrappedApp = (props: AppProps) => {
  useReportWebVitals((m) => console.log('Web Vitals', m))

  return (
    <>
      <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_ID ?? 'GTM-MXRZSF5B'} />
      <MyApp {...props} />
    </>
  )
}

export default WrappedApp
